import type { IntervalDataType } from '@flipster/r230522/original/interval/interval-key';
import { getTickerDisplayPrice, type TickerPriceSource } from '@flipster/r230522/original/ticker-price';

/**
 * Perpetual chart price source persisted in TradingViewDataManager.
 *
 * ROLLBACK(last-price): MM 준비 완료 시 재활성화 — 원본: `'LAST' | 'MARK'`, 기본값 `'LAST'`.
 * MM 미준비로 perp 차트를 pre-feature 상태(MID 기본 + MID/MARK 토글)로 임시 회귀.
 */
// export type ChartPriceType = 'LAST' | 'MARK';
export type ChartPriceType = 'MID' | 'MARK';

// export const defaultChartPriceType: ChartPriceType = 'LAST';
export const defaultChartPriceType: ChartPriceType = 'MID';

/** Spot chart uses BBO mid — VARA spot has no reliable last-trade candles.
 * @see https://prextech.slack.com/archives/C0AQX0CL77F/p1784089299975669?thread_ts=1783388487.251749&cid=C0AQX0CL77F
 */
export const spotChartPriceType: IntervalDataType = 'MID';

export function normalizeChartPriceType(value: unknown): ChartPriceType {
  if (value === 'MARK') return 'MARK';
  // ROLLBACK(last-price): 피쳐 기간에 저장된 'LAST'도 MID로 흡수 (마이그레이션 불요). 원본: return 'LAST';
  return 'MID';
}

/** Returns the alternate chart price source (MID ↔ MARK). */
export function invertChartPriceType(priceType: ChartPriceType): ChartPriceType {
  // ROLLBACK(last-price): 원본: return priceType === 'MARK' ? 'LAST' : 'MARK';
  return priceType === 'MARK' ? 'MID' : 'MARK';
}

/**
 * Normalize interval price type for chart composite keys / WS routing.
 * - Spot always uses BBO mid (`MID`). Native mid convention is no suffix; do not map that to LAST.
 * - Perp: `MARK` 유지, 나머지(no-suffix / `MID` / 피쳐 기간 해시의 `LAST`)는 전부 `MID`.
 *
 * ROLLBACK(last-price): MM 준비 완료 시 재활성화 — 원본 perp 매핑:
 *   legacy `MID` → `LAST`; no-suffix / `LAST` → `LAST`; `MARK` → `MARK`.
 *
 * @see https://prextech.slack.com/archives/C0AQX0CL77F/p1784089299975669
 */
export function normalizeIntervalDataType(
  value: IntervalDataType | string | undefined,
  symbol?: string,
): IntervalDataType {
  // Spot never adopted Last Price — force MID even when hash/legacy path carries :LAST or no suffix.
  if (symbol != null && !symbol.endsWith('.PERP')) return 'MID';

  if (value === 'MARK') return 'MARK';
  // // Perp legacy MID → LAST after last-price migration.
  // if (value === 'MID') return 'LAST';
  // if (value === 'LAST' || value == null) return 'LAST';
  return 'MID';
}

/** TV ticker label: omit `:LAST` / `:MID` suffix (MARK keeps `:MARK`). Display-only — not a LAST-alias check. */
export function isLastBasedChartPrice(priceType: IntervalDataType | ChartPriceType): boolean {
  return priceType === 'LAST' || priceType === 'MID';
}

/**
 * WS/REST interval price type for chart symbols.
 * Pass through: spot/perp request native MID; perp MARK stays MARK.
 * ROLLBACK(last-price): 원본 — spot은 MID, perp는 LAST/MARK 요청.
 */
export function toIntervalTransportPriceType(priceType: IntervalDataType): IntervalDataType {
  return priceType;
}

export function getChartLinkedTickerPrice(
  ticker: (TickerPriceSource & { markPrice?: string }) | null | undefined,
  priceType: ChartPriceType,
): string | undefined {
  if (!ticker) return undefined;
  if (priceType === 'MARK') return ticker.markPrice;
  return getTickerDisplayPrice(ticker);
}
